import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from './AccountContext';
import './SpecialDaysPhotoEDMs.css';
import ImageWithAccountInfo from './ImageWithAccountInfo';
import TemplateFooterVariants from './components/TemplateFooterVariants';
import SchedulePostModal from '../../Dashboard/components/SchedulePostModal';
import GMBService from '../../../services/GMBService';
import ApiService from '../../../services/api';

import logger from '../../../utils/logger';
const templateData = [
  // World Refugee Day
  ['home.jpg', 'Plot  on sale.png', 'car.jpg'],
  // Happy Father's Day
  ['Export  Web Development services.png', 'car.jpg', 'home.jpg'],
  // Happy Mother's Day
  ['Export  Web Development services.png', 'car.jpg', 'home.jpg'],
  // World Wind Day
  ['Export  Web Development services.png', 'car.jpg', 'home.jpg'],
];

const templateNames = [
  'World Refugee Day',
  'Happy Father\'s Day',
  'Happy Mother\'s Day',
  'World Wind Day',
];

const shareOptions = [
  { icon: 'fas fa-envelope', label: 'Email' },
  { icon: 'fab fa-facebook', label: 'Facebook' },
  { icon: 'fab fa-whatsapp', label: 'WhatsApp' },
  { icon: 'fab fa-instagram', label: 'Instagram' },
  { icon: 'fab fa-google', label: 'Google Profile' },
  { icon: 'fas fa-link', label: 'Copy link' },
];

const SpecialDaysPhotoEDMs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { uploadedLogo, isLogoUploaded, loadAccountInfo, accountInfo, selectedFontStyle } = useAccount();
  const selectedSpecialDay = location.state?.selectedSpecialDay || 'World Refugee Day';
  
  // Load account info when component mounts
  useEffect(() => {
    loadAccountInfo();
  }, []);

  // Function to get font family based on selected font style
  const getFontFamily = (fontStyle) => {
    const fontMappings = {
      'Modern': 'Arial, Helvetica, sans-serif',
      'Elegant': 'Georgia, Times New Roman, serif',
      'Slab': 'Roboto Slab, Georgia, serif',
      'Hand Written': 'Cursive, Brush Script MT, cursive',
      'Playful': 'Comic Sans MS, cursive, sans-serif',
      'Futuristic': 'Orbitron, Arial, sans-serif'
    };
    return fontMappings[fontStyle] || 'Arial, sans-serif';
  };
  
  // Find the index of the selected special day
  const initialTemplateIndex = templateNames.findIndex(name => 
    name.toLowerCase() === selectedSpecialDay.toLowerCase()
  );
  
  const [currentTemplate, setCurrentTemplate] = useState(
    initialTemplateIndex >= 0 ? initialTemplateIndex : 0
  );
  const [imageOrder, setImageOrder] = useState(templateData.map(() => [0, 1, 2]));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [mainImg, setMainImg] = useState(templateData[initialTemplateIndex >= 0 ? initialTemplateIndex : 0][1]);
  const [selectedFooterId, setSelectedFooterId] = useState(1);
  const [logoPosition, setLogoPosition] = useState(50); // Logo position (0-100)
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [customFooterColor, setCustomFooterColor] = useState(() => {
    try {
      const savedColor = localStorage.getItem('visibeen.customFooterColor');
      return savedColor || '#4A90E2';
    } catch (e) {
      return '#4A90E2';
    }
  });
  const [customFontColor, setCustomFontColor] = useState(() => {
    try {
      const savedColor = localStorage.getItem('visibeen.customFontColor');
      return savedColor || '#ffffff';
    } catch (e) {
      return '#ffffff';
    }
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [schedulePostModalOpen, setSchedulePostModalOpen] = useState(false);
  const [capturedImageForSchedule, setCapturedImageForSchedule] = useState(null);
  const [gmbAccountId, setGmbAccountId] = useState(null);
  const [gmbLocationId, setGmbLocationId] = useState(null);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const dropdownRef = useRef(null);
  const shareRef = useRef(null);
  const largePreviewRef = useRef(null);
  
  // Update mainImg when template changes
  useEffect(() => {
    setMainImg(templateData[currentTemplate][1]);
  }, [currentTemplate]);

  // Read selected footer id from localStorage (persisted by SelectDesign)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('visibeen.selectedFooterId');
      const parsed = raw != null ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(parsed) && parsed > 0) {
        setSelectedFooterId(parsed);
      }
    } catch (e) {
      logger.debug('[SpecialDaysPhotoEDMs] could not read selected footer id', e);
    }
  }, []);

  // Load GMB account and location IDs on component mount (use same keys as Dashboard)
  useEffect(() => {
    const loadGMBInfo = async () => {
      try {
        // Use the same localStorage keys as Dashboard and PostGenerationResults
        const savedAccount = localStorage.getItem('selectedGMBAccount');
        const savedLocation = localStorage.getItem('selectedGMBLocation');
        
        if (savedAccount && savedLocation) {
          const account = JSON.parse(savedAccount);
          const location = JSON.parse(savedLocation);
          
          const accountId = account.name?.split('/').pop();
          const locationId = location.name?.split('/').pop();
          
          if (accountId && locationId) {
            setGmbAccountId(accountId);
            setGmbLocationId(locationId);
            setSelectedProfile({
              accountId,
              locationId,
              businessName: location.title || account.accountName,
              address: location.storefrontAddress?.formattedAddress || '',
              isVerified: true
            });
            logger.log('[SpecialDaysPhotoEDMs] ✅ Loaded GMB profile from Dashboard selection:', {
              accountId,
              locationId,
              businessName: location.title
            });
            return;
          }
        }
        
        logger.log('[SpecialDaysPhotoEDMs] No saved GMB profile found - please select one from Dashboard');
      } catch (error) {
        logger.error('[SpecialDaysPhotoEDMs] Error loading GMB info:', error);
      }
    };
    
    loadGMBInfo();
  }, []);

  // Function to fetch all available GMB profiles (via backend to avoid CORS)
  const fetchGMBProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const accessToken = await GMBService.getAccessToken();
      if (!accessToken) {
        alert('Please sign in with Google first to access your business profiles.');
        return [];
      }

      logger.log('[SpecialDaysPhotoEDMs] Fetching GMB accounts (backend)...');
      const accountsResp = await ApiService.get(`/customer/profile/getGmbAccount?googleAccessToken=${encodeURIComponent(accessToken)}`);
      const accountsRaw = accountsResp?.data || accountsResp;
      const accounts = Array.isArray(accountsRaw) ? accountsRaw : (accountsRaw?.accounts || []);

      if (!accounts || accounts.length === 0) {
        alert('No Google My Business accounts found. Please set up your GMB account first.');
        return [];
      }

      const profiles = [];

      for (const account of accounts) {
        try {
          const accountMatch = account.name?.match(/accounts\/(\d+)/);
          const accountId = accountMatch ? accountMatch[1] : account.name?.split('/').pop();

          logger.log('[SpecialDaysPhotoEDMs] Fetching locations (backend) for account:', accountId);
          const locResp = await ApiService.get(`/customer/profile/getGmbProfile/${accountId}?googleAccessToken=${encodeURIComponent(accessToken)}`);
          const locationsRaw = locResp?.data || locResp;
          const locations = Array.isArray(locationsRaw) ? locationsRaw : (locationsRaw?.locations || []);

          for (const location of locations) {
            const locationMatch = location.name?.match(/locations\/(\d+)/);
            const locationId = locationMatch ? locationMatch[1] : location.name?.split('/').pop();

            profiles.push({
              accountId: accountId,
              locationId: locationId,
              businessName: location.title || location.locationName || 'Unnamed Business',
              address: formatAddress(location.storefrontAddress || location.address),
              category: location.categories?.primaryCategory?.displayName || location.primaryCategory?.displayName || 'Business',
              phoneNumber: location.phoneNumbers?.primaryPhone || location.primaryPhone || '',
              isVerified: !!location.metadata,
              fullLocationData: location
            });
          }
        } catch (err) {
          logger.error('[SpecialDaysPhotoEDMs] Error fetching locations for account:', account?.name, err);
        }
      }

      logger.log('[SpecialDaysPhotoEDMs] Profiles loaded:', profiles.length);
      setAvailableProfiles(profiles);
      return profiles;
    } catch (error) {
      logger.error('[SpecialDaysPhotoEDMs] Error fetching GMB profiles:', error);
      alert('Failed to load GMB profiles. Please try again.');
      return [];
    } finally {
      setLoadingProfiles(false);
    }
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return 'No address';
    if (typeof address === 'string') return address;
    
    const parts = [];
    if (address.addressLines) parts.push(...address.addressLines);
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    
    return parts.join(', ') || 'No address';
  };

  // Handle profile selection
  const handleProfileSelect = (profile) => {
    setGmbAccountId(profile.accountId);
    setGmbLocationId(profile.locationId);
    setSelectedProfile(profile);
    
    // Note: We don't need to save to localStorage here - use Dashboard's "Set as Default" instead
    
    logger.log('[SpecialDaysPhotoEDMs] Profile selected:', profile);
    setShowProfileSelector(false);
    
    setTimeout(() => {
      handleCaptureAndSchedule();
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setShareOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateTemplate = (idx) => {
    setCurrentTemplate(idx);
    setDropdownOpen(false);
    setImageOrder([0, 1, 2]); // Reset image order when changing template
  };

  const rotateLeft = () => {
    setImageOrder((prev) => {
      const newOrder = [...prev];
      const order = newOrder[currentTemplate];
      newOrder[currentTemplate] = [order[1], order[2], order[0]];
      return newOrder;
    });
  };
  const rotateRight = () => {
    setImageOrder((prev) => {
      const newOrder = [...prev];
      const order = newOrder[currentTemplate];
      newOrder[currentTemplate] = [order[2], order[0], order[1]];
      return newOrder;
    });
  };

  // Logo interaction functions
  const handleLogoMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  const handleLogoMouseMove = (e) => {
    if (!isDraggingLogo) return;
    
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setLogoPosition(percentage);
  };

  const handleLogoMouseUp = () => {
    setIsDraggingLogo(false);
  };

  const moveLogoLeft = () => {
    setLogoPosition(prev => Math.max(0, prev - 10));
  };

  const moveLogoRight = () => {
    setLogoPosition(prev => Math.min(100, prev + 10));
  };

  // Color picker functions
  const handleColorChange = (color) => {
    setCustomFooterColor(color);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleOpenSchedulePostModal = async () => {
    logger.log('[SpecialDaysPhotoEDMs] Opening schedule post modal...');
    
    // Check if GMB profile is selected
    if (!gmbAccountId || !gmbLocationId) {
      logger.log('[SpecialDaysPhotoEDMs] No GMB profile selected');
      alert('Please select a default GMB profile first.\n\nGo to Dashboard → Click "Set as Default" on your business profile.');
      setShareOpen(false);
      return;
    }
    
    // Profile is selected, proceed with capture and schedule
    handleCaptureAndSchedule();
  };

  const handleCaptureAndSchedule = async () => {
    // Capture the post as image first
    const postElement = largePreviewRef.current;
    if (!postElement) {
      logger.error('[SpecialDaysPhotoEDMs] Post preview element not found');
      return;
    }
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(postElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      const file = new File([blob], `special-day-edm-${Date.now()}.png`, { type: 'image/png' });
      
      // Open schedule post modal directly with the captured image
      setCapturedImageForSchedule(file);
      setSchedulePostModalOpen(true);
      setShareOpen(false); // Close share menu
      
      logger.log('[SpecialDaysPhotoEDMs] Schedule post modal opened with template image');
    } catch (err) {
      logger.error('[SpecialDaysPhotoEDMs] Error capturing post:', err);
      alert('Failed to capture image. Please try again.');
    }
  };

  const handleCloseSchedulePostModal = () => {
    setSchedulePostModalOpen(false);
    setCapturedImageForSchedule(null);
  };

  const handlePostScheduled = () => {
    logger.log('[SpecialDaysPhotoEDMs] Post scheduled successfully');
    setSchedulePostModalOpen(false);
    setCapturedImageForSchedule(null);
    alert('Post scheduled successfully to Google My Business!');
  };

  const downloadImage = async () => {
    try {
      if (!largePreviewRef.current) {
        logger.error('Large preview container not found');
        return;
      }

      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(largePreviewRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        removeContainer: true
      });

      const link = document.createElement('a');
      link.download = `visibeen-edm-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      logger.error('Error downloading image:', error);
      // Fallback: try to download the original image
      const imgElement = document.querySelector('.large-preview img');
      if (imgElement) {
        const link = document.createElement('a');
        link.href = imgElement.src;
        link.download = `visibeen-edm-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <>
      {/* Main Content */}
      <div className="main-content">
        <div className="main-title">
          <div className="products-title">Special Days EDMs</div>
          <div className="products-subtitle">Select your Template</div>
        </div>
        {/* Dropdown */}
        <div className="template-section">
          <div className={`custom-dropdown${dropdownOpen ? ' open' : ''}`} ref={dropdownRef}>
            <div className="custom-dropdown-selected" onClick={() => setDropdownOpen((open) => !open)}>
              {templateNames[currentTemplate]} <span className="dropdown-arrow">&#9660;</span>
            </div>
            <ul className="custom-dropdown-list">
              {templateNames.map((name, idx) => (
                <li key={name} onClick={() => updateTemplate(idx)}>{name}</li>
              ))}
            </ul>
          </div>
        </div>
        {/* Card Previews */}
        <div className="card-preview-row">
          {templateData[currentTemplate].map((img, idx) => (
            <div key={`${img}-${idx}`} className={`card-preview${mainImg === img ? ' selected' : ''}`} onClick={() => {
              // Set the clicked image as the main preview
              setMainImg(img);
            }}>
              <div style={{ position: 'relative' }}>
                <ImageWithAccountInfo imageSrc={require(`./${img}`)} imageIndex={idx} isMainPreview={false} 
                   logoPosition={logoPosition} isDraggingLogo={isDraggingLogo} 
                   handleLogoMouseDown={handleLogoMouseDown} handleLogoMouseMove={handleLogoMouseMove} handleLogoMouseUp={handleLogoMouseUp}
                   customFooterColor={customFooterColor} customFontColor={customFontColor} getFontFamily={getFontFamily} showFooterOverlay={false}
                />
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                  <TemplateFooterVariants designId={selectedFooterId} accountInfo={accountInfo} customColor={customFooterColor} customFontColor={customFontColor} />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Large Preview */}
        <div className="large-preview">
          <div ref={largePreviewRef} style={{ position: 'relative' }}>
            <ImageWithAccountInfo imageSrc={require(`./${mainImg}`)} imageIndex={templateData[currentTemplate].indexOf(mainImg)} isMainPreview={true} 
                logoPosition={logoPosition} isDraggingLogo={isDraggingLogo} 
                handleLogoMouseDown={handleLogoMouseDown} handleLogoMouseMove={handleLogoMouseMove} handleLogoMouseUp={handleLogoMouseUp}
                customFooterColor={customFooterColor} customFontColor={customFontColor} getFontFamily={getFontFamily} showFooterOverlay={false}
            />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
              <TemplateFooterVariants designId={selectedFooterId} accountInfo={accountInfo} customColor={customFooterColor} customFontColor={customFontColor} />
            </div>
          </div>
        </div>
        {/* Selected GMB Profile Indicator */}
        {selectedProfile && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            marginTop: '20px',
            padding: '15px 20px',
            backgroundColor: 'rgba(66, 133, 244, 0.1)',
            borderRadius: '12px',
            border: '2px solid rgba(66, 133, 244, 0.3)'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                <i className="fab fa-google" style={{ marginRight: '6px', color: '#4285F4' }}></i>
                Selected GMB Profile:
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                {selectedProfile.businessName}
              </div>
            </div>
            <button
              onClick={() => setShowProfileSelector(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4285F4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'background-color 0.3s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#3367D6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4285F4'}
            >
              Change Profile
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-download" onClick={downloadImage}>Download</button>
          <div className="share-btn-wrapper" ref={shareRef}>
            <button className="btn-share" onClick={() => setShareOpen((open) => !open)} id="shareBtn">Share</button>
            {shareOpen && (
              <div className="share-menu" id="shareMenu">
                <ul>
                  {shareOptions.map((opt, idx) => (
                    <li 
                      key={opt.label}
                      onClick={() => {
                        if (opt.label === 'Google Profile') {
                          handleOpenSchedulePostModal();
                        }
                      }}
                      style={{ cursor: opt.label === 'Google Profile' ? 'pointer' : 'default' }}
                    >
                      <i className={opt.icon}></i> {opt.label} {opt.extra || null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {/* Logo Control Buttons */}
        {uploadedLogo && isLogoUploaded && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginTop: '20px',
            padding: '15px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)'
          }}>
            <button 
              onClick={moveLogoLeft}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
            >
              ← Move Logo Left
            </button>
            <div style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(0,0,0,0.7)', 
              color: 'white', 
              borderRadius: '5px', 
              fontSize: '14px', 
              fontWeight: 'bold' 
            }}> 
              Position: {Math.round(logoPosition)}% 
            </div> 
            <button  
              onClick={moveLogoRight} 
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#2196F3', 
                color: 'white', 
                border: 'none', 
                borderRadius: '5px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontWeight: 'bold', 
                transition: 'background-color 0.3s ease' 
              }} 
              onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'} 
              onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'} 
            > 
              Move Logo Right → 
            </button> 
          </div> 
        )} 
         
        {/* Color Pickers Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginTop: '20px',
          padding: '20px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          {/* Footer Color Picker */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '140px' }}>
              Footer Background:
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="color"
                value={customFooterColor}
                onChange={(e) => setCustomFooterColor(e.target.value)}
                style={{
                  width: '50px',
                  height: '35px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{
              padding: '6px 12px',
              backgroundColor: customFooterColor,
              color: customFontColor,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              minWidth: '90px',
              textAlign: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}>
              {customFooterColor}
            </div>
            <button
              onClick={() => setCustomFooterColor('#4A90E2')}
              style={{
                padding: '6px 14px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e0e0e0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            >
              Reset
            </button>
          </div>

          {/* Font Color Picker */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap',
            paddingTop: '10px',
            borderTop: '1px solid #eee'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '140px' }}>
              Font Color:
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="color"
                value={customFontColor}
                onChange={(e) => setCustomFontColor(e.target.value)}
                style={{
                  width: '50px',
                  height: '35px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{
              padding: '6px 12px',
              backgroundColor: customFooterColor,
              color: customFontColor,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              minWidth: '90px',
              textAlign: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}>
              {customFontColor}
            </div>
            <button
              onClick={() => setCustomFontColor('#ffffff')}
              style={{
                padding: '6px 14px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e0e0e0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            >
              Reset
            </button>
          </div>

          {/* Quick Color Presets */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #eee',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#666' }}>Quick Presets:</span>
            {[
              { bg: '#000000', font: '#ffffff', label: 'Dark' },
              { bg: '#ffffff', font: '#000000', label: 'Light' },
              { bg: '#1e3a5f', font: '#ffffff', label: 'Navy' },
              { bg: '#8b0000', font: '#ffd700', label: 'Royal' },
              { bg: '#2d5a27', font: '#ffffff', label: 'Forest' }
            ].map((preset, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setCustomFooterColor(preset.bg);
                  setCustomFontColor(preset.font);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: preset.bg,
                  color: preset.font,
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Color Picker Modal */}
        {showColorPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }} onClick={toggleColorPicker}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              minWidth: '300px'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>Choose Footer Color</h3>
              
              {/* Preset Colors */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Preset Colors:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                  {['#4A90E2', '#E74C3C', '#2ECC71', '#F39C12', '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#FF6B6B', '#4ECDC4'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange(color)}
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: color,
                        border: '2px solid #ddd',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              {/* Custom Color Picker */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Custom Color:</h4>
                <input
                  type="color"
                  value={customFooterColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '2px solid #ddd',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                />
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={toggleColorPicker}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6C757D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleColorChange('#4A90E2');
                    toggleColorPicker();
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#DC3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Reset to Default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Post Modal for Google My Business */}
      <SchedulePostModal
        open={schedulePostModalOpen}
        onClose={handleCloseSchedulePostModal}
        onSuccess={handlePostScheduled}
        preloadedImage={capturedImageForSchedule}
        initialContent={`${selectedSpecialDay}! 🎉\n\nCelebrating this special day with gratitude and joy.\n\n${accountInfo?.businessName ? `- ${accountInfo.businessName}` : ''}`}
        accountId={gmbAccountId}
        locationId={gmbLocationId}
        businessCategory={accountInfo?.category || ''}
        businessName={accountInfo?.businessName || ''}
        businessAddress={accountInfo?.address || ''}
        businessKeywords={accountInfo?.keywords || []}
        businessCoordinates={accountInfo?.latlng || null}
        postName={selectedSpecialDay || 'Special Day Post'}
      />
      </div>
    </>
  );
};

export default SpecialDaysPhotoEDMs; 
